import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../../api/axios';
import { useLoading } from '../../../../components/LoadingProvider';
import useAuth from '../../../../hooks/useAuth';
import { showSuccess, showError } from '../../../../utils/toast';
import { getIndStates } from '../../../../utils/common';

const cerealOptions = [
    'Rice',
    'Atta',
    'Maida',
    'Suzi',
    'Biscuits',
    'Muri',
    'Sago',
    'Chira',
    'Cornflakes',
    'Bread',
    'Bajra',
    'Noodles',
    'Jowar',
    'Oats',
];

const fruitOptions = ['Citrus', 'Non-Citrus'];
const milkOptions = ['Skimmed milk', 'Toned milk'];

const createAvoidItem = (value = '', checked = true, source = 'user') => ({
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    item: value,
    checked,
    source,
});

const buildAvoidItemsFromConfig = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) {
        return [createAvoidItem('', false, 'misc')];
    }
    return items.map((item) => ({
        id: item?.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        item: typeof item === 'string' ? item : (item?.item || item?.name || ''),
        checked: item?.checked !== false,
        source: item?.source || 'misc',
    }));
};

export default function DoctorAppointmentDietPrescriptionPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    document.title = `Varosa - Doctor Appointment Diet Prescription No ${id}`;
    const { setIsLoading } = useLoading();
    const { userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['doctor'] });
    const [ appointmentPrescriptionData, setAppointmentPrescriptionData ] = useState(null);
    const [ oldAppointments, setOldAppointments ] = useState([]);
    const prefixOfName = [ 'Dr.', 'Prof.', 'Mr.', 'Mrs.', 'Ms.' ];
    const [ accordionStatus, setAccordionStatus ] = useState({
        one: true,
    });
    const formPatientRef = useRef(null);
    const formRef = useRef(null);
    const indStates = getIndStates();
    const [ prescriptionFormData, setPrescriptionFormData ] = useState({
        patient_name: '',
        patient_name_prefix: '',
        patient_dob: '',
        patient_gender: '',
        patient_address_line1: '',
        patient_address_line2: '',
        patient_address_district: '',
        patient_address_state: '',
        patient_address_pincode: '',
        cereals: [],
        dal_and_dal_products: '',
        green_leafy_vegetables: '',
        other_vegetables: '',
        fruits: '',
        fish: '',
        chicken: '',
        egg: '',
        oil: '',
        milk: '',
        foods_to_avoid: [],
        remarks: '',
        is_completed: 'Yes',
        prescription_type: 'diet_prescription',
    });
    const [ prescriptionFormError, setPrescriptionFormError ] = useState({
        patient_name: '',
        patient_name_prefix: '',
        patient_dob: '',
        patient_gender: '',
        patient_address_line1: '',
        patient_address_district: '',
        patient_address_state: '',
        patient_address_pincode: '',
        cereals: '',
        dal_and_dal_products: '',
        green_leafy_vegetables: '',
        other_vegetables: '',
        fruits: '',
        fish: '',
        chicken: '',
        egg: '',
        oil: '',
        milk: '',
        remarks: '',
        is_completed: '',
    });
    useEffect(() => {
        fetchAppointmentPrescription();
    }, []);
    const normalizeState = (stateValue) => {
        let found_state = stateValue || '';
        if(found_state.length > 2){
            let ind_state = indStates.find(s => {
                return found_state.replaceAll(' ', '').toLocaleLowerCase() === s.name.replaceAll(' ', '').toLocaleLowerCase();
            });
            if(ind_state){
                found_state = ind_state.key;
            }
        }
        return found_state;
    }
    const normalizeAvoidItems = (items = [], fallbackItems = []) => {
        const fallbackLookup = new Map(
            buildAvoidItemsFromConfig(fallbackItems).map((item) => [
                item.item.toLocaleLowerCase().trim(),
                item,
            ])
        );
        if (Array.isArray(items) && items.length > 0) {
            return items.map((item) => ({
                id: item?.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                item: typeof item === 'string' ? item : (item?.item || item?.name || ''),
                checked: item?.checked !== false,
                source: item?.source || fallbackLookup.get((typeof item === 'string' ? item : (item?.item || item?.name || '')).toLocaleLowerCase().trim())?.source || 'user',
            }));
        }
        return buildAvoidItemsFromConfig(fallbackItems);
    }
    const fetchAppointmentPrescription = async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/appointment/${id}/diet-prescription`);
            if (response?.data?.result === 'success') {
                setAppointmentPrescriptionData(response?.data?.data);
                setOldAppointments(response?.data?.data.old_appointments || []);
                const patient = response?.data?.data?.patient || {};
                const appointmentPrescription = response?.data?.data?.appointment_prescription || {};
                const foodsToAvoidDefaults = response?.data?.data?.misc?.foods_to_avoid || [];
                setPrescriptionFormData({
                    patient_name: patient?.name || '',
                    patient_name_prefix: patient?.name_prefix || '',
                    patient_dob: patient?.dob || '',
                    patient_gender: patient?.gender || '',
                    patient_address_line1: patient?.address_line1 || '',
                    patient_address_line2: patient?.address_line2 || '',
                    patient_address_district: patient?.district || '',
                    patient_address_state: normalizeState(patient?.state || ''),
                    patient_address_pincode: patient?.pincode || '',
                    cereals: Array.isArray(appointmentPrescription?.cereals) ? appointmentPrescription.cereals : [],
                    dal_and_dal_products: appointmentPrescription?.dal_and_dal_products || '',
                    green_leafy_vegetables: appointmentPrescription?.green_leafy_vegetables || '',
                    other_vegetables: appointmentPrescription?.other_vegetables || '',
                    fruits: appointmentPrescription?.fruits || '',
                    fish: appointmentPrescription?.fish || '',
                    chicken: appointmentPrescription?.chicken || '',
                    egg: appointmentPrescription?.egg || '',
                    oil: appointmentPrescription?.oil || '',
                    milk: appointmentPrescription?.milk || '',
                    foods_to_avoid: normalizeAvoidItems(appointmentPrescription?.foods_to_avoid || [], foodsToAvoidDefaults),
                    remarks: appointmentPrescription?.remarks || '',
                    is_completed: 'Yes',
                    prescription_type: 'diet_prescription',
                });
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors?.redirect){
                    navigate(`/dashboard/${userRole}/appointment/${id}`);
                }else if(response?.data?.errors){
                    response?.data?.errors.forEach((item) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const handleAppointmentDateChange = async (e) => {
        const { value } = e.target;
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/appointment/${id}/old-prescription?type=diet_prescription&search_date=` + value);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                setOldAppointments(response?.data?.data.old_appointments || []);
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const toggleArrayValue = (field, value) => {
        const current = Array.isArray(prescriptionFormData?.[field]) ? prescriptionFormData[field] : [];
        const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
        setPrescriptionFormData({ ...prescriptionFormData, [field]: next });
    }
    const updateAvoidItem = (index, key, value) => {
        const next = [...(prescriptionFormData?.foods_to_avoid || [])];
        next[index] = { ...next[index], [key]: value };
        setPrescriptionFormData({ ...prescriptionFormData, foods_to_avoid: next });
    }
    const addAvoidItem = () => {
        setPrescriptionFormData({
            ...prescriptionFormData,
            foods_to_avoid: [...(prescriptionFormData?.foods_to_avoid || []), createAvoidItem('', true, 'user')],
        });
    }
    const removeAvoidItem = (index) => {
        const next = [...(prescriptionFormData?.foods_to_avoid || [])].filter((_, currentIndex) => currentIndex !== index);
        setPrescriptionFormData({
            ...prescriptionFormData,
            foods_to_avoid: next.length > 0 ? next : [createAvoidItem('', false, 'misc')],
        });
    }
    const handlePrescription = async () => {
        try {
            const resetPrescriptionFormError = {
                patient_name: '',
                patient_name_prefix: '',
                patient_dob: '',
                patient_gender: '',
                patient_address_line1: '',
                patient_address_district: '',
                patient_address_state: '',
                patient_address_pincode: '',
                cereals: '',
                dal_and_dal_products: '',
                green_leafy_vegetables: '',
                other_vegetables: '',
                fruits: '',
                fish: '',
                chicken: '',
                egg: '',
                oil: '',
                milk: '',
                remarks: '',
                is_completed: '',
            };
            setPrescriptionFormError(resetPrescriptionFormError);
            if (!prescriptionFormData?.patient_name) return setPrescriptionFormError({ ...resetPrescriptionFormError, patient_name: 'Patient Name is required' });
            if (!prescriptionFormData?.patient_name_prefix) return setPrescriptionFormError({ ...resetPrescriptionFormError, patient_name_prefix: 'Patient Name Prefix is required' });
            if (!prescriptionFormData?.patient_dob) return setPrescriptionFormError({ ...resetPrescriptionFormError, patient_dob: 'Patient DOB is required' });
            if (!prescriptionFormData?.patient_gender) return setPrescriptionFormError({ ...resetPrescriptionFormError, patient_gender: 'Patient Gender is required' });
            if (!prescriptionFormData?.patient_address_line1) return setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_line1: 'Patient Address line 1 is required' });
            if (!prescriptionFormData?.patient_address_district) return setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_district: 'Patient District is required' });
            if (!prescriptionFormData?.patient_address_state) return setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_state: 'Patient State is required' });
            if (!prescriptionFormData?.patient_address_pincode) return setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_pincode: 'Patient Pincode is required' });
            if (!prescriptionFormData?.cereals?.length) return setPrescriptionFormError({ ...resetPrescriptionFormError, cereals: 'Cereals is required' });
            if (!prescriptionFormData?.dal_and_dal_products) return setPrescriptionFormError({ ...resetPrescriptionFormError, dal_and_dal_products: 'Dal and Dal Products is required' });
            if (!prescriptionFormData?.green_leafy_vegetables) return setPrescriptionFormError({ ...resetPrescriptionFormError, green_leafy_vegetables: 'Green leafy vegetables is required' });
            if (!prescriptionFormData?.other_vegetables) return setPrescriptionFormError({ ...resetPrescriptionFormError, other_vegetables: 'Other vegetables is required' });
            if (!prescriptionFormData?.fruits) return setPrescriptionFormError({ ...resetPrescriptionFormError, fruits: 'Fruits is required' });
            if (!prescriptionFormData?.fish && !prescriptionFormData?.chicken){
                if (!prescriptionFormData?.fish) return setPrescriptionFormError({ ...resetPrescriptionFormError, fish: 'Fish is required' });
                if (!prescriptionFormData?.chicken) return setPrescriptionFormError({ ...resetPrescriptionFormError, chicken: 'Chicken is required' });
            }

            if (!prescriptionFormData?.egg) return setPrescriptionFormError({ ...resetPrescriptionFormError, egg: 'Egg is required' });
            if (!prescriptionFormData?.oil) return setPrescriptionFormError({ ...resetPrescriptionFormError, oil: 'Oil is required' });
            if (!prescriptionFormData?.milk) return setPrescriptionFormError({ ...resetPrescriptionFormError, milk: 'Milk is required' });
            if (!prescriptionFormData?.is_completed) return setPrescriptionFormError({ ...resetPrescriptionFormError, is_completed: 'Appointment Completed is required' });

            setIsLoading(true);
            const response = await api.post(`/${userRole}/appointment/${id}/diet-prescription`, prescriptionFormData);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                fetchAppointmentPrescription();
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors?.redirect){
                    navigate(`/dashboard/${userRole}/appointment/${id}`);
                }else if(response?.data?.errors){
                    response?.data?.errors.forEach((item) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const renderFoodItem = (item, index) => (
        <tr key={item?.id || index}>
            <td style={{ width: '10%', verticalAlign: 'middle', textAlign: 'center' }}>
                <div className="form-check" style={{ justifySelf: 'center' }}>
                    <input
                        className="form-check-input"
                        type="checkbox"
                        checked={item?.checked !== false}
                        onChange={(e) => updateAvoidItem(index, 'checked', e.target.checked)}
                    />
                </div>
            </td>
            <td style={{ width: '80%' }}>
                <input
                    type="text"
                    className="form-control py-2 border-primary"
                    value={item?.item || ''}
                    onChange={(e) => updateAvoidItem(index, 'item', e.target.value)}
                    placeholder="Enter food to avoid"
                />
                <small className="text-muted">
                    {item?.source === 'misc' ? 'Default from template' : 'Added by doctor'}
                </small>
            </td>
            <td style={{ width: '10%' }}>
                <button type="button" className="btn btn-danger btn-sm" onClick={() => removeAvoidItem(index)}>Remove</button>
            </td>
        </tr>
    );
    return (
        <div className="container py-5 login-container appointment-detail">
            <div className="row g-3">
                <div className="col-8">
                    <h2 className="mb-4">Appointment Diet Prescription No {id}</h2>
                </div>
                <div className="col-4 text-end">
                    <button onClick={() => window.history.go(-1)} className="btn btn-primary">Back</button>
                </div>
                <hr />
            </div>

            <div className="row g-3">
                <div className="col-md-12">
                    <div className="accordion">
                        <div className="accordion-item">
                            <h2
                                className="accordion-header"
                                id="panelsStayOpen-headingOne"
                                onClick={() => setAccordionStatus({ ...accordionStatus, one: !accordionStatus.one })}
                            >
                                <button className={`accordion-button ${!accordionStatus.one ? 'collapsed' : ''}`} type="button">
                                    Previous Appointments
                                </button>
                            </h2>
                            <div id="panelsStayOpen-collapseOne" className={`accordion-collapse collapse ${accordionStatus.one ? 'show' : ''}`}>
                                <div className="accordion-body">
                                    <div className="row">
                                        <div className="col-12">
                                            <label className="form-label">Select Appointment Date</label>
                                            <input
                                                type="date"
                                                className="form-control py-3 border-primary"
                                                onChange={handleAppointmentDateChange}
                                                max={new Date().toISOString().split('T')[0]}
                                            />
                                        </div>
                                        <hr className="mt-4 mb-3" />
                                        <div className="col-12">
                                            {oldAppointments.length > 0 ? (
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th scope="col" style={{ width: '10%' }}>IDs</th>
                                                            <th scope="col" style={{ width: '30%' }}>Appointment Details</th>
                                                            <th scope="col" style={{ width: '30%' }}>Patient Details</th>
                                                            <th scope="col" style={{ width: '30%' }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {oldAppointments?.map((item, index) => (
                                                            <tr key={index}>
                                                                <th scope="row" style={{ width: '10%' }}>{item.appointment.id}</th>
                                                                <td style={{ width: '30%' }}>
                                                                    Date & Time: <b>{item.appointment.appointment_date_time}</b><br />
                                                                    Appointment Completed: <b>{item.appointment.is_completed}</b>
                                                                </td>
                                                                <td style={{ width: '30%' }}>
                                                                    Name: <b>{item.appointment.patient_name}</b><br />
                                                                    Age: <b>{item.appointment.patient_age} Yrs</b><br />
                                                                    Gender: <b>{item.appointment.patient_gender.toUpperCase()}</b><br />
                                                                    Address: <b>{item.appointment.patient_address}</b>
                                                                </td>
                                                                <td style={{ width: '30%' }}>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary btn-sm mt-1 mb-1 me-1"
                                                                        onClick={() => {
                                                                            const patient = item?.patient || {};
                                                                            const prescription = item?.appointment_prescription || {};
                                                                            const foodsToAvoidDefaults = appointmentPrescriptionData?.misc?.foods_to_avoid || [];
                                                                            setPrescriptionFormData({
                                                                                ...prescriptionFormData,
                                                                                patient_name_prefix: patient?.name_prefix || '',
                                                                                patient_name: patient?.name || '',
                                                                                patient_dob: patient?.dob || '',
                                                                                patient_gender: patient?.gender || '',
                                                                                patient_address_line1: patient?.address_line1 || '',
                                                                                patient_address_line2: patient?.address_line2 || '',
                                                                                patient_address_district: patient?.district || '',
                                                                                patient_address_state: normalizeState(patient?.state || ''),
                                                                                patient_address_pincode: patient?.pincode || '',
                                                                                cereals: Array.isArray(prescription?.cereals) ? prescription.cereals : [],
                                                                                dal_and_dal_products: prescription?.dal_and_dal_products || '',
                                                                                green_leafy_vegetables: prescription?.green_leafy_vegetables || '',
                                                                                other_vegetables: prescription?.other_vegetables || '',
                                                                                fruits: prescription?.fruits || '',
                                                                                fish: prescription?.fish || '',
                                                                                chicken: prescription?.chicken || '',
                                                                                egg: prescription?.egg || '',
                                                                                oil: prescription?.oil || '',
                                                                                milk: prescription?.milk || '',
                                                                                foods_to_avoid: normalizeAvoidItems(prescription?.foods_to_avoid || [], foodsToAvoidDefaults),
                                                                                remarks: prescription?.remarks || '',
                                                                            });
                                                                            setPrescriptionFormError({
                                                                                ...prescriptionFormError,
                                                                                patient_name_prefix: '',
                                                                                patient_name: '',
                                                                                patient_dob: '',
                                                                                patient_gender: '',
                                                                                patient_address_line1: '',
                                                                                patient_address_district: '',
                                                                                patient_address_state: '',
                                                                                patient_address_pincode: '',
                                                                                cereals: '',
                                                                                dal_and_dal_products: '',
                                                                                green_leafy_vegetables: '',
                                                                                other_vegetables: '',
                                                                                fruits: '',
                                                                                fish: '',
                                                                                chicken: '',
                                                                                egg: '',
                                                                                oil: '',
                                                                                milk: '',
                                                                                remarks: '',
                                                                            });
                                                                            formPatientRef.current?.scrollIntoView({ behavior: 'smooth' });
                                                                        }}
                                                                    >Copy Patient Data</button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-success btn-sm mt-1 mb-1 me-1"
                                                                        onClick={() => {
                                                                            const patient = item?.patient || {};
                                                                            const prescription = item?.appointment_prescription || {};
                                                                            const foodsToAvoidDefaults = appointmentPrescriptionData?.misc?.foods_to_avoid || [];
                                                                            setPrescriptionFormData({
                                                                                ...prescriptionFormData,
                                                                                patient_name_prefix: patient?.name_prefix || '',
                                                                                patient_name: patient?.name || '',
                                                                                patient_dob: patient?.dob || '',
                                                                                patient_gender: patient?.gender || '',
                                                                                patient_address_line1: patient?.address_line1 || '',
                                                                                patient_address_line2: patient?.address_line2 || '',
                                                                                patient_address_district: patient?.district || '',
                                                                                patient_address_state: normalizeState(patient?.state || ''),
                                                                                patient_address_pincode: patient?.pincode || '',
                                                                                cereals: Array.isArray(prescription?.cereals) ? prescription.cereals : [],
                                                                                dal_and_dal_products: prescription?.dal_and_dal_products || '',
                                                                                green_leafy_vegetables: prescription?.green_leafy_vegetables || '',
                                                                                other_vegetables: prescription?.other_vegetables || '',
                                                                                fruits: prescription?.fruits || '',
                                                                                fish: prescription?.fish || '',
                                                                                chicken: prescription?.chicken || '',
                                                                                egg: prescription?.egg || '',
                                                                                oil: prescription?.oil || '',
                                                                                milk: prescription?.milk || '',
                                                                                foods_to_avoid: normalizeAvoidItems(prescription?.foods_to_avoid || [], foodsToAvoidDefaults),
                                                                                remarks: prescription?.remarks || '',
                                                                            });
                                                                            setPrescriptionFormError({
                                                                                ...prescriptionFormError,
                                                                                patient_name_prefix: '',
                                                                                patient_name: '',
                                                                                patient_dob: '',
                                                                                patient_gender: '',
                                                                                patient_address_line1: '',
                                                                                patient_address_district: '',
                                                                                patient_address_state: '',
                                                                                patient_address_pincode: '',
                                                                                cereals: '',
                                                                                dal_and_dal_products: '',
                                                                                green_leafy_vegetables: '',
                                                                                other_vegetables: '',
                                                                                fruits: '',
                                                                                fish: '',
                                                                                chicken: '',
                                                                                egg: '',
                                                                                oil: '',
                                                                                milk: '',
                                                                                remarks: '',
                                                                            });
                                                                            formRef.current?.scrollIntoView({ behavior: 'smooth' });
                                                                        }}
                                                                    >Copy Prescription Data</button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                                    Previous Appointments not found!
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-12" ref={formPatientRef}>
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title">Prescription Details</h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <p className="card-text"><strong>Patient ID:</strong> {appointmentPrescriptionData?.appointment?.patient_id}</p>
                                </div>
                                <div className="col-md-6">
                                    <p className="card-text"><strong>Consultation ID:</strong> {appointmentPrescriptionData?.appointment?.id}</p>
                                </div>
                                <div className="col-md-6">
                                    <p className="card-text"><strong>Consultation Date:</strong> {appointmentPrescriptionData?.appointment?.appointment_date_time}</p>
                                </div>
                                <div className="col-md-6">
                                    <p className="card-text"><strong>Doctor Name:</strong> {appointmentPrescriptionData?.appointment?.doctor_name}</p>
                                </div>
                                <hr />
                                <div className="col-md-6">
                                    <label className="form-label">Name</label>
                                    <div className="input-group mb-3">
                                        <select
                                            className="form-select py-3 border-primary"
                                            value={prescriptionFormData?.patient_name_prefix}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_name_prefix: e.target.value })}
                                            style={{ maxWidth: '120px' }}
                                        >
                                            <option value="">Choose...</option>
                                            {prefixOfName.map((prefix) => (
                                                <option key={prefix} value={prefix}>{prefix}</option>
                                            ))}
                                        </select>
                                        <input
                                            type="text"
                                            className="form-control py-3 border-primary"
                                            value={prescriptionFormData?.patient_name}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_name: e.target.value })}
                                        />
                                    </div>
                                    {prescriptionFormError.patient_name && <div className="invalid-feedback d-block">{prescriptionFormError.patient_name}</div>}
                                    {prescriptionFormError.patient_name_prefix && <div className="invalid-feedback d-block">{prescriptionFormError.patient_name_prefix}</div>}
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">DOB</label>
                                    <input
                                        type="date"
                                        className="form-control py-3 border-primary"
                                        value={prescriptionFormData?.patient_dob}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_dob: e.target.value })}
                                        max={new Date().toISOString().split('T')[0]}
                                    />
                                    {prescriptionFormError.patient_dob && <div className="invalid-feedback d-block">{prescriptionFormError.patient_dob}</div>}
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Gender</label>
                                    <select
                                        className="form-select py-3 border-primary"
                                        value={prescriptionFormData?.patient_gender}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_gender: e.target.value })}
                                    >
                                        <option value="">Choose...</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="other">Other</option>
                                    </select>
                                    {prescriptionFormError.patient_gender && <div className="invalid-feedback d-block">{prescriptionFormError.patient_gender}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Address Line 1</label>
                                    <input
                                        type="text"
                                        className="form-control py-3 border-primary"
                                        value={prescriptionFormData?.patient_address_line1}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_line1: e.target.value })}
                                    />
                                    {prescriptionFormError.patient_address_line1 && <div className="invalid-feedback d-block">{prescriptionFormError.patient_address_line1}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Address Line 2 (optional)</label>
                                    <input
                                        type="text"
                                        className="form-control py-3 border-primary"
                                        value={prescriptionFormData?.patient_address_line2}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_line2: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">District</label>
                                    <input
                                        type="text"
                                        className="form-control py-3 border-primary"
                                        value={prescriptionFormData?.patient_address_district}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_district: e.target.value })}
                                    />
                                    {prescriptionFormError.patient_address_district && <div className="invalid-feedback d-block">{prescriptionFormError.patient_address_district}</div>}
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">State</label>
                                    <select
                                        className="form-select py-3 border-primary"
                                        value={prescriptionFormData?.patient_address_state}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_state: e.target.value })}
                                    >
                                        <option value="">Choose...</option>
                                        {indStates.map((val) => (<option key={val.key} value={val.key}>{val.name}</option>))}
                                    </select>
                                    {prescriptionFormError.patient_address_state && <div className="invalid-feedback d-block">{prescriptionFormError.patient_address_state}</div>}
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Pincode</label>
                                    <input
                                        type="text"
                                        className="form-control py-3 border-primary"
                                        value={prescriptionFormData?.patient_address_pincode}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_pincode: e.target.value })}
                                    />
                                    {prescriptionFormError.patient_address_pincode && <div className="invalid-feedback d-block">{prescriptionFormError.patient_address_pincode}</div>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-12" ref={formRef}>
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title">Diet Prescription Form</h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <label className="form-label fw-bold">Cereals</label>
                                    <div className="row g-2">
                                        {cerealOptions.map((item) => (
                                            <div className="col-md-3 col-6" key={item}>
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        checked={prescriptionFormData?.cereals?.includes(item)}
                                                        onChange={() => toggleArrayValue('cereals', item)}
                                                        id={`cereal-${item}`}
                                                    />
                                                    <label className="form-check-label" htmlFor={`cereal-${item}`}>{item}</label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {prescriptionFormError.cereals && <div className="text-danger mt-2">{prescriptionFormError.cereals}</div>}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Dal and dal products</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={prescriptionFormData?.dal_and_dal_products}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, dal_and_dal_products: e.target.value })}
                                    />
                                    {prescriptionFormError.dal_and_dal_products && <div className="text-danger">{prescriptionFormError.dal_and_dal_products}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Green leafy vegetables</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={prescriptionFormData?.green_leafy_vegetables}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, green_leafy_vegetables: e.target.value })}
                                    />
                                    {prescriptionFormError.green_leafy_vegetables && <div className="text-danger">{prescriptionFormError.green_leafy_vegetables}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Other vegetables</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={prescriptionFormData?.other_vegetables}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, other_vegetables: e.target.value })}
                                    />
                                    {prescriptionFormError.other_vegetables && <div className="text-danger">{prescriptionFormError.other_vegetables}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Fruits</label>
                                    <div className="d-flex gap-4">
                                        {fruitOptions.map((item) => (
                                            <div className="form-check" key={item}>
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="fruits"
                                                    id={`fruits-${item}`}
                                                    checked={prescriptionFormData?.fruits === item}
                                                    onChange={() => setPrescriptionFormData({ ...prescriptionFormData, fruits: item })}
                                                />
                                                <label className="form-check-label" htmlFor={`fruits-${item}`}>{item}</label>
                                            </div>
                                        ))}
                                    </div>
                                    {prescriptionFormError.fruits && <div className="text-danger">{prescriptionFormError.fruits}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Fish</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={prescriptionFormData?.fish}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, fish: e.target.value })}
                                    />
                                    {prescriptionFormError.fish && <div className="text-danger">{prescriptionFormError.fish}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Chicken</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={prescriptionFormData?.chicken}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, chicken: e.target.value })}
                                    />
                                    {prescriptionFormError.chicken && <div className="text-danger">{prescriptionFormError.chicken}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Egg</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={prescriptionFormData?.egg}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, egg: e.target.value })}
                                    />
                                    {prescriptionFormError.egg && <div className="text-danger">{prescriptionFormError.egg}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Oil</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={prescriptionFormData?.oil}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, oil: e.target.value })}
                                    />
                                    {prescriptionFormError.oil && <div className="text-danger">{prescriptionFormError.oil}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Milk</label>
                                    <div className="d-flex gap-4">
                                        {milkOptions.map((item) => (
                                            <div className="form-check" key={item}>
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="milk"
                                                    id={`milk-${item}`}
                                                    checked={prescriptionFormData?.milk === item}
                                                    onChange={() => setPrescriptionFormData({ ...prescriptionFormData, milk: item })}
                                                />
                                                <label className="form-check-label" htmlFor={`milk-${item}`}>{item}</label>
                                            </div>
                                        ))}
                                    </div>
                                    {prescriptionFormError.milk && <div className="text-danger">{prescriptionFormError.milk}</div>}
                                </div>

                                <div className="col-md-12">
                                    <label className="form-label fw-bold">Foods to avoid</label>
                                    <div className="table-responsive">
                                        <table className="table table-bordered align-middle">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '10%' }}>Enable</th>
                                                    <th>Food Item</th>
                                                    <th style={{ width: '10%' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {prescriptionFormData?.foods_to_avoid?.map((item, index) => renderFoodItem(item, index))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="3">
                                                        <button type="button" className="btn btn-primary" onClick={addAvoidItem}>Add</button>
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label htmlFor="remarks" className="form-label">Remarks</label>
                                        <textarea className="form-control" id="remarks" rows="3" value={prescriptionFormData?.remarks} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, remarks: e.target.value })}></textarea>
                                        {prescriptionFormError?.remarks && <div className="text-danger">{prescriptionFormError?.remarks}</div>}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Appointment Completed</label>
                                    <select
                                        className="form-select border-primary"
                                        value={prescriptionFormData?.is_completed}
                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, is_completed: e.target.value })}
                                    >
                                        {['Yes', 'No'].map((item) => (
                                            <option key={item} value={item}>{item}</option>
                                        ))}
                                    </select>
                                    {prescriptionFormError.is_completed && <div className="text-danger">{prescriptionFormError.is_completed}</div>}
                                </div>
                            </div>
                            <button type="button" className="btn btn-primary float-end mt-5" onClick={handlePrescription}>Submit</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
